<template>
  <DeepLoaderAnimation v-if="isLoadingInitial" />
  <template
    v-else-if="
      !!overview &&
      !!deepBoxCoreStore.selectedBox &&
      !!deepBoxBoxInfoStore.accessCompany?.companyId
    "
  >
    <!-- MENU -->
    <v-menu
      v-if="totalBoxesCount > 1"
      v-model="menu"
      :close-on-content-click="false"
    >
      <!-- MENU BUTTON -->
      <template #activator="{ props: ActivatorProps }">
        <v-btn
          v-if="totalBoxesCount > 1"
          v-bind="ActivatorProps"
          class="px-0 mx-2"
          data-test-id="box-navigation-selection"
          :ripple="false"
          variant="text"
        >
          <BoxItemList
            :box="deepBoxCoreStore.selectedBox"
            is-box-selection-menu
            :is-shared="isSharedBox"
            show-box-name-subtitle
          >
            <template #append>
              <v-icon
                class="pa-0 ma-0"
                :class="device?.isTablet ? 'ml-0' : 'ml-1'"
                color="grey"
              >
                {{ menu ? 'far fa-angle-up' : 'far fa-angle-down' }}
              </v-icon>
            </template>
          </BoxItemList>
        </v-btn>
        <BoxItemList
          v-else
          :box="deepBoxCoreStore.selectedBox"
          is-box-selection-menu
          :is-shared="isSharedBox"
          show-box-name-subtitle
        />
      </template>
      <!-- /MENU BUTTON -->

      <!-- MENU CARD -->
      <v-card class="d-flex flex-column pa-0" max-height="830" max-width="300">
        <v-card-text v-if="showMenuLoading" class="d-flex justify-center">
          <v-progress-circular
            color="grey"
            indeterminate
            :size="20"
            width="2"
          />
        </v-card-text>
        <template v-else>
          <v-card-item>
            <v-text-field
              v-model="search"
              autocomplete="off"
              autofocus
              clearable
              density="compact"
              hide-details
              :loading="isApiLoading"
              name="field-search-box"
              :placeholder="t('placeholders.search_box')"
            />
          </v-card-item>

          <v-card-text class="flex-grow-1 overflow-y-auto">
            <v-list
              class="pa-0 boxes-list"
              density="compact"
              slim
              variant="text"
            >
              <PerfectScrollbar :options="{ suppressScrollX: true }">
                <template v-if="hasBoxes">
                  <template v-if="!search">
                    <!-- RECENTS -->
                    <template
                      v-if="
                        overview?.recents.boxes.length && totalBoxesCount > 10
                      "
                    >
                      <v-list-subheader>
                        {{ t('labels.recents') }}
                      </v-list-subheader>

                      <BoxItemList
                        v-for="recentBox in overview?.recents.boxes.slice(0, 3)"
                        :key="`recent-${recentBox.company?.companyId}-${recentBox.deepBoxNodeId}-${recentBox.boxNodeId}`"
                        :box="recentBox"
                        :box-avatar-box-size="16"
                        class="pl-1"
                        is-box-selection-menu
                        is-recent
                        :is-selected="isBoxSelected(recentBox)"
                        show-favorite
                        show-selected
                        :to="{
                          name: 'organization-types-type-boxes-box-files',
                          params: {
                            type: recentBox.deepBoxNodeId,
                            box: recentBox.boxNodeId,
                          },
                        }"
                        :value="`recent-${recentBox.company?.companyId}-${recentBox.deepBoxNodeId}-${recentBox.boxNodeId}`"
                        @click="selectBox(recentBox)"
                      ></BoxItemList>
                    </template>
                    <!-- /RECENTS -->

                    <!-- FAVORITES -->
                    <template v-if="favoriteBoxes.length">
                      <v-list-subheader>
                        {{ t('labels.favorites') }}
                      </v-list-subheader>
                      <BoxItemList
                        v-for="favoriteBox in favoriteBoxes"
                        :key="`favorite-${favoriteBox.company?.companyId}-${favoriteBox.deepBoxNodeId}-${favoriteBox.boxNodeId}`"
                        :box="favoriteBox"
                        :box-avatar-box-size="16"
                        class="pl-1"
                        is-box-selection-menu
                        is-favourite
                        :is-selected="isBoxSelected(favoriteBox)"
                        show-favorite
                        show-selected
                        :to="{
                          name: 'organization-types-type-boxes-box-files',
                          params: {
                            type: favoriteBox.deepBoxNodeId,
                            box: favoriteBox.boxNodeId,
                          },
                        }"
                        :value="`favorite-${favoriteBox.company?.companyId}-${favoriteBox.deepBoxNodeId}-${favoriteBox.boxNodeId}`"
                        @click="selectBox(favoriteBox)"
                      ></BoxItemList>
                    </template>
                    <!-- /FAVORITES -->
                  </template>

                  <!-- TYPES BOXES -->
                  <div v-for="type in typeBoxes" :key="type.deepBoxNodeId">
                    <v-list-subheader v-if="type.boxes.length > 0">
                      <DeepLink
                        class="text-truncate deep-link"
                        :to="{
                          name: 'organization-type',
                          params: {
                            organization: type.company.companyId,
                            type: type.deepBoxNodeId,
                          },
                        }"
                      >
                        {{ type.deepBoxName }}
                      </DeepLink>
                    </v-list-subheader>
                    <BoxItemList
                      v-for="typeBox in type.boxes"
                      :key="`box-${typeBox.company.companyId}-${typeBox.deepBoxNodeId}-${typeBox.boxNodeId}`"
                      :box="typeBox"
                      :box-avatar-box-size="16"
                      class="pl-1"
                      :data-test-id="`box-item-${typeBox.boxName}`"
                      is-box-selection-menu
                      :is-selected="isBoxSelected(typeBox)"
                      show-favorite
                      show-selected
                      :to="{
                        name: 'organization-types-type-boxes-box-files',
                        params: {
                          type: typeBox.deepBoxNodeId,
                          box: typeBox.boxNodeId,
                        },
                      }"
                      :value="`box-${typeBox.company.companyId}-${typeBox.deepBoxNodeId}-${typeBox.boxNodeId}`"
                      @click="selectBox(typeBox)"
                    ></BoxItemList>
                    <div v-if="type.size > type.boxes.length" class="mt-1">
                      <v-btn
                        color="primary"
                        data-test-id="more-boxes"
                        :loading="fetchNextBoxesPending"
                        variant="text"
                        @click="showMoreBoxesByType(type)"
                      >
                        {{ t('actions.more') }}
                      </v-btn>
                    </div>
                    <!-- /ITEM -->
                  </div>
                  <!-- /TYPES BOXES -->

                  <!-- SHARED BOXES -->
                  <div v-if="sharedBoxes.length > 0" class="mt-4">
                    <v-list-subheader>
                      {{ t('labels.boxes_shared_with_me') }}
                    </v-list-subheader>

                    <BoxItemList
                      v-for="sharedBox in sharedBoxes.slice(0, 6)"
                      :key="`share-${sharedBox.company?.companyId}-${sharedBox.deepBoxNodeId}-${sharedBox.boxNodeId}`"
                      :append-icon="
                        deepBoxCoreStore.selectedBox &&
                        sharedBox.boxNodeId ===
                          deepBoxCoreStore.selectedBox.boxNodeId
                          ? 'far fa-check'
                          : undefined
                      "
                      :box="sharedBox"
                      :box-avatar-box-size="16"
                      class="pl-1"
                      is-box-selection-menu
                      :is-selected="isBoxSelected(sharedBox)"
                      is-shared
                      :show-box-name-subtitle="
                        hasMoreBoxesFromSameOrg(sharedBox)
                      "
                      show-favorite
                      show-selected
                      :to="{
                        name: 'organization-types-type-boxes-box-files',
                        params: {
                          type: sharedBox.deepBoxNodeId,
                          box: sharedBox.boxNodeId,
                        },
                      }"
                      :value="`share-${sharedBox.company?.companyId}-${sharedBox.deepBoxNodeId}-${sharedBox.boxNodeId}`"
                      @click="selectBox(sharedBox)"
                    ></BoxItemList>

                    <div v-if="sharedBoxes.length > 6" class="mt-1">
                      <v-btn
                        color="primary"
                        :to="toHome"
                        variant="text"
                        @click="onGoToHome"
                      >
                        {{ t('actions.more') }}
                      </v-btn>
                    </div>
                  </div>
                  <!-- /SHARED BOXES -->
                </template>
                <v-list-item
                  v-else
                  class="pa-0"
                  :title="t('messages.no_boxes_found')"
                />
              </PerfectScrollbar>
            </v-list>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-btn
              block
              color="primary"
              data-test-id="open-dashboard"
              :to="toHome"
              variant="text"
              @click="onGoToHome"
            >
              {{ t('labels.open_dashboard') }}
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
      <!-- MENU CARD -->
    </v-menu>
    <BoxItemList
      v-else
      :box="deepBoxCoreStore.selectedBox"
      is-box-selection-menu
      :is-shared="isSharedBox"
      show-box-name-subtitle
    />
  </template>

  <!-- MENU -->
</template>
<script lang="ts" setup>
import { toast } from 'vue-sonner'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import debounce from 'lodash/debounce'
import { DeepLink, DeepLoaderAnimation } from '@deepcloud/deep-ui-lib'
import { useDeepAdminUsersMeStore } from '@/stores/deepadmin/users/users-me'
import { useDeepBoxBoxInfoStore } from '@/stores/deepbox/box-info'
import { useDeepBoxCoreStore } from '@/stores/deepbox/core'
import { useDeepBoxDeepBoxesBoxesFavoritesStore } from '@/stores/deepbox/deepboxes/boxes/favorites.ts'
import { useDeepBoxDeepBoxesBoxesFilesTreeStore } from '@/stores/deepbox/deepboxes/boxes/files-tree.ts'
import { DeviceKey } from '@/plugins/device-detector-js.ts'
import { useApi } from '@/composables/api.ts'
import { useBoxHelper } from '@/composables/box/box-helper'
import { useContextBoxOverview } from '@/composables/context/ctx-box-overview'
import BoxItemList from '@/components/box/BoxItemList.vue'
import type {
  BoxEntry,
  BoxInfo,
  DeepBoxOverview,
  Overview,
  OverviewQueryParams,
} from '@deepcloud/api-deep-box'

const { t } = useI18n()
const { deepBoxAPI } = useApi()

const device = inject(DeviceKey)

const deepBoxCoreStore = useDeepBoxCoreStore()
const deepAdminUsersMeStore = useDeepAdminUsersMeStore()
const deepBoxBoxInfoStore = useDeepBoxBoxInfoStore()
const deepBoxDeepBoxesBoxesFavoritesStore =
  useDeepBoxDeepBoxesBoxesFavoritesStore()
const deepBoxDeepBoxesBoxesFilesTreeStore =
  useDeepBoxDeepBoxesBoxesFilesTreeStore()

const { overview: contextOverview } = useContextBoxOverview()

const menu = ref(false)
const search = ref('')

const overview = ref<Overview>()

// only updated by non-search fetches, so totalBoxesCount stays stable while searching
const unfilteredOverview = ref<Overview>()

// seed from the box-info overview already fetched by LayoutBoxDetail, instead
// of firing a dedicated request just to know the initial box count
watch(
  contextOverview,
  (value) => {
    if (value && !overview.value) {
      overview.value = value
    }
    if (value && !unfilteredOverview.value) {
      unfilteredOverview.value = value
    }
  },
  { immediate: true },
)

const sharedBoxes = computed(() => overview.value?.sharedWithMe?.boxes || [])

const typeBoxes = computed(() => overview.value?.deepBoxes || [])

const favoriteBoxes = computed(() =>
  search.value?.length > 0
    ? overview.value?.favorites?.boxes || []
    : deepBoxDeepBoxesBoxesFavoritesStore.favorites || [],
)

const hasBoxes = computed(() => {
  if (
    typeBoxes.value &&
    typeBoxes.value.length === 0 &&
    sharedBoxes.value &&
    sharedBoxes.value.length === 0
  ) {
    return false
  }
  if (sharedBoxes.value.length > 0) {
    return true
  }
  const typesWithBoxes = typeBoxes.value.filter(
    (type) => type?.boxes?.length > 0,
  )
  return typesWithBoxes.length > 0
})

const boxHelper = useBoxHelper()

const toHome = computed(() => {
  return {
    name: 'organization',
    params: {
      organization: deepAdminUsersMeStore.selectedOrganizationId,
    },
  }
})

const isApiLoading = ref(false)

async function onFetchOverview(
  companyId: string,
  options?: { query?: string; limit?: number },
) {
  isApiLoading.value = true
  try {
    const params: OverviewQueryParams = {
      'deepBoxes.boxes.limit': options?.limit ? options.limit : 10,
      companyId,
    }
    if (options?.query) {
      params.q = options.query
    }
    const res = await deepBoxAPI.value.overview.get(params)

    if (res.data) {
      overview.value = res.data
      if (!options?.query) {
        unfilteredOverview.value = res.data
      }
    }
  } catch {
    toast.error(t('error.error_occurred'))
  } finally {
    isApiLoading.value = false
  }
}

const isLoadingInitial = computed(() => !overview.value)

const showMenuLoading = ref(false)

watch(
  () => menu.value,
  async (newValue) => {
    if (newValue && deepBoxBoxInfoStore.accessCompany?.companyId) {
      showMenuLoading.value = true
      await onFetchOverview(deepBoxBoxInfoStore.accessCompany.companyId)
      showMenuLoading.value = false
    } else {
      search.value = ''
    }
  },
)

watch(
  () => search.value,
  (newValue) => {
    searchBoxesDebounced(newValue)
  },
)

const searchBoxesDebounced = debounce(function (search) {
  if (!deepBoxBoxInfoStore.accessCompany?.companyId) return
  onFetchOverview(deepBoxBoxInfoStore.accessCompany.companyId, {
    query: search,
  })
}, 500)

function getOverviewTotalBoxesCount(o: Overview) {
  const typeBoxes = computed(() => o?.deepBoxes || [])
  const sharedBoxes = o?.sharedWithMe?.boxes || []

  let total = typeBoxes.value
    .flatMap((type) => type.size)
    .reduce((a, b) => a + b, 0)

  total += sharedBoxes.length

  return total
}

const totalBoxesCount = computed(() =>
  unfilteredOverview.value
    ? getOverviewTotalBoxesCount(unfilteredOverview.value)
    : 0,
)

function selectBox(box: BoxEntry) {
  boxHelper.selectBox(box)
  menu.value = false
  deepBoxDeepBoxesBoxesFilesTreeStore.isFilesTreeOpened = false
}

function isBoxSelected(box: BoxEntry) {
  return (
    deepBoxCoreStore.selectedBox &&
    box.boxNodeId === deepBoxCoreStore.selectedBox.boxNodeId
  )
}

const isSharedBox = computed(() => {
  return (
    !!overview.value &&
    !!deepBoxCoreStore.selectedBox &&
    !!deepBoxBoxInfoStore.accessCompany &&
    boxHelper.isSharedBox(
      deepBoxCoreStore.selectedBox as BoxInfo,
      overview.value.sharedWithMe.boxes,
      deepBoxBoxInfoStore.accessCompany?.companyId,
    )
  )
})

function onGoToHome() {
  // be sure that displayTree is closed when go to home
  deepBoxDeepBoxesBoxesFilesTreeStore.isFilesTreeOpened = false
}

function hasMoreBoxesFromSameOrg(item: BoxEntry) {
  const orgId = item?.company?.companyId
  const foundOrganizations = [...sharedBoxes.value].filter(
    (box) => box?.company?.companyId === orgId,
  )

  return foundOrganizations.length > 1
}

const fetchNextBoxesPending = ref(false)

async function showMoreBoxesByType(type: DeepBoxOverview) {
  fetchNextBoxesPending.value = true
  try {
    const params = {
      limit: 50,
      offset: type.boxes.length,
    }
    const res = await deepBoxAPI.value.overviewDeepBoxes.get(
      type.deepBoxNodeId,
      params,
    )
    const resData = res.data

    resData.boxes.forEach((box) => {
      type.boxes.push(box)
    })

    return Promise.resolve(res)
  } catch (error) {
    return Promise.reject(error)
  } finally {
    fetchNextBoxesPending.value = false
  }
}
</script>
<style scoped lang="scss">
@import 'vue3-perfect-scrollbar/style.css';
.boxes-list {
  :deep(.v-list-subheader) {
    padding-inline-start: 0 !important;
  }
}

.deep-link {
  color: inherit !important;
  font-weight: inherit !important;
  display: inline-block !important;
}
</style>
